import { useRef, useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import type { InboundExpectedEditReason } from '@/infrastructure/FulfillmentAdminClient';

/** The line whose expectation is being rewritten, and the floor the new figure may not fall under. */
export interface EditExpectedQtyTarget {
  docId: string;
  inboundNo: string;
  lineNo: number;
  product: string;
  qtyExpected: number;
  qtyReceived: number;
  /** The row as the desk read it; the save carries it back so a moved line is refused [T05]. */
  updatedAt?: string;
}

export interface EditExpectedQtyModalProps {
  target: EditExpectedQtyTarget;
  busy: boolean;
  onClose: () => void;
  onConfirm: (qty: number, reason: InboundExpectedEditReason, memo: string) => void;
}

/** Byte-exact option labels; a QA assertion needs one exact string [BR-53]. */
const REASONS: Array<{ value: InboundExpectedEditReason; label: string }> = [
  { value: 'DAMAGED_DEFECTIVE', label: 'Damaged/defective — cannot accept' },
  { value: 'SUPPLIER_QTY_CHANGE', label: 'Supplier qty change' },
  { value: 'OTHER', label: 'Other' },
];

const MEMO_PLACEHOLDER = 'e.g. 1 box damaged — 180 units rejected, returned to supplier';

/**
 * The new figure may never fall under what the floor counted: that is an over-receipt no screen can clear [E-24].
 * Laid out like Cancel Inbound: the line on a strip at the top, three numbered fields stacked, the buttons in
 * the footer — the quantity box says what the change means as it is typed, and the floor is stated before it
 * is hit.
 */
export function EditExpectedQtyModal({ target, busy, onClose, onConfirm }: EditExpectedQtyModalProps) {
  const [qty, setQty] = useState(String(target.qtyExpected));
  // Nothing is said about the figure until a hand has touched it: the opening value is the current one.
  const [touched, setTouched] = useState(false);
  const [reason, setReason] = useState<InboundExpectedEditReason | ''>('');
  const [memo, setMemo] = useState('');
  const dialogRef = useModalDialog(onClose);
  const reasonRef = useRef<HTMLSelectElement>(null);

  // Digits only, read as a safe integer: a scanner or a keyboard types digits, never a sign or an exponent.
  const digits = /^\d+$/.test(qty.trim());
  const parsed = digits ? Number(qty.trim()) : Number.NaN;
  const counted = digits && Number.isSafeInteger(parsed) && parsed >= 1;
  const belowReceived = counted && parsed < target.qtyReceived;
  const changed = counted && parsed !== target.qtyExpected;
  const blocked = busy || !counted || belowReceived || reason === '' || !changed;
  const diff = counted ? parsed - target.qtyExpected : 0;
  const save = () => {
    if (reason === '') {
      // Enter on a figure nobody has explained yet: the next thing to fill is the reason, so the cursor goes there.
      reasonRef.current?.focus();
      return;
    }
    if (blocked) {
      return;
    }
    onConfirm(parsed, reason, memo);
  };

  return (
    <dialog className="vo-modal" ref={dialogRef} aria-label="Edit Expected Qty">
      <header>
        <b>Edit Expected Qty</b>
        <span className="hdr-sub">Inbound No. {target.inboundNo}</span>
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body cim">
        <div className="eeq-line">
          <b>{target.product}</b>
          <span className="eeq-facts">
            <span>
              Expected <b>{target.qtyExpected}</b>
            </span>
            <span>
              Received <b>{target.qtyReceived}</b>
            </span>
          </span>
        </div>

        <div className="cim-field">
          <label className="cim-label" htmlFor="eeq-qty">
            <span className="cim-n">1</span>New Expected Qty
          </label>
          <div className="eeq-qtyrow">
            <input
              id="eeq-qty"
              className="eeq-qty"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              aria-label="New Expected Qty"
              value={qty}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) => {
                setTouched(true);
                setQty(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  save();
                }
              }}
            />
            {changed && !belowReceived ? (
              <span className={diff > 0 ? 'eeq-delta up' : 'eeq-delta down'} role="status">
                {target.qtyExpected} → {parsed} · {Math.abs(diff)} {diff > 0 ? 'more' : 'fewer'} than requested
              </span>
            ) : (
              <span className="eeq-delta same">
                {touched && counted && !belowReceived ? 'Same as now — nothing to save' : ''}
                {touched && !counted && qty.trim() !== '' ? 'Whole number of units, please' : ''}
              </span>
            )}
          </div>
          {belowReceived ? (
            <p className="cim-need" role="alert">
              New expected qty cannot be lower than the received qty ({target.qtyReceived})
            </p>
          ) : (
            <p className="cim-help">
              {target.qtyReceived > 0
                ? `Cannot go below the ${target.qtyReceived} already received. Fewer arrived than requested? Lower it to what came in, and the request completes by itself.`
                : 'Fewer arrived than requested? Lower it to what came in, and the request completes by itself.'}
            </p>
          )}
        </div>

        <div className="cim-field">
          <label className="cim-label" htmlFor="eeq-reason">
            <span className="cim-n">2</span>Reason <span className="cim-opt">(Required)</span>
          </label>
          <select
            id="eeq-reason"
            ref={reasonRef}
            className="cim-input"
            aria-label="Reason (required)"
            value={reason}
            onChange={(event) => setReason(event.target.value as InboundExpectedEditReason | '')}
          >
            <option value="">Choose a reason…</option>
            {REASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="cim-help">Written into the request thread with the change, so the requester knows why.</p>
        </div>

        <div className="cim-field">
          <label className="cim-label" htmlFor="eeq-memo">
            <span className="cim-n">3</span>Memo <span className="cim-opt">(Optional)</span>
          </label>
          <textarea
            id="eeq-memo"
            className="cim-input eeq-memo"
            aria-label="Memo"
            rows={3}
            placeholder={MEMO_PLACEHOLDER}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-green btn-sm" disabled={blocked} onClick={save}>
          Save Qty Edit
        </button>
      </div>
    </dialog>
  );
}
